import { Calendar, CalendarClock, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/Button';

export type ScheduleCalendarView = 'monthly' | 'weekly' | 'day';

const HEADER_OUTLINE_BUTTON_CLASS =
  'border-white/40 bg-white/10 text-white hover:bg-white/20 hover:text-white shadow-sm';

interface ScheduleViewHeaderToggleProps {
  value: ScheduleCalendarView;
  onChange: (view: ScheduleCalendarView) => void;
  /** Outline buttons on gradient header (default). Pass empty string for default dashboard buttons. */
  variant?: 'header' | 'default';
  /** Which views to show. Defaults to monthly, weekly, and day. */
  availableViews?: ScheduleCalendarView[];
}

const DEFAULT_AVAILABLE_VIEWS: ScheduleCalendarView[] = ['monthly', 'weekly', 'day'];

export function ScheduleViewHeaderToggle({
  value,
  onChange,
  variant = 'default',
  availableViews = DEFAULT_AVAILABLE_VIEWS,
}: ScheduleViewHeaderToggleProps) {
  const headerClass = variant === 'header' ? HEADER_OUTLINE_BUTTON_CLASS : undefined;
  const monthlySelected = value === 'monthly';
  const weeklySelected = value === 'weekly';
  const daySelected = value === 'day';
  const showMonthly = availableViews.includes('monthly');
  const showWeekly = availableViews.includes('weekly');
  const showDay = availableViews.includes('day');

  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Schedule view">
      {showMonthly ? (
      <Button
        type="button"
        variant={monthlySelected ? 'default' : 'outline'}
        size="sm"
        onClick={() => onChange('monthly')}
        className={`gap-1.5 ${!monthlySelected && headerClass ? headerClass : ''} ${
          monthlySelected && variant === 'header'
            ? 'bg-white text-gray-900 shadow-sm hover:bg-white/90'
            : ''
        }`}
        aria-pressed={monthlySelected}
      >
        <Calendar className="h-4 w-4" aria-hidden />
        <span className="hidden sm:inline">Monthly</span>
      </Button>
      ) : null}
      {showWeekly ? (
      <Button
        type="button"
        variant={weeklySelected ? 'default' : 'outline'}
        size="sm"
        onClick={() => onChange('weekly')}
        className={`gap-1.5 ${!weeklySelected && headerClass ? headerClass : ''} ${
          weeklySelected && variant === 'header'
            ? 'bg-white text-gray-900 shadow-sm hover:bg-white/90'
            : ''
        }`}
        aria-pressed={weeklySelected}
      >
        <CalendarClock className="h-4 w-4" aria-hidden />
        <span className="hidden sm:inline">Weekly</span>
      </Button>
      ) : null}
      {showDay ? (
      <Button
        type="button"
        variant={daySelected ? 'default' : 'outline'}
        size="sm"
        onClick={() => onChange('day')}
        className={`gap-1.5 ${!daySelected && headerClass ? headerClass : ''} ${
          daySelected && variant === 'header'
            ? 'bg-white text-gray-900 shadow-sm hover:bg-white/90'
            : ''
        }`}
        aria-pressed={daySelected}
      >
        <CalendarDays className="h-4 w-4" aria-hidden />
        <span className="hidden sm:inline">Day</span>
      </Button>
      ) : null}
    </div>
  );
}
